import React, { useState, useRef, useEffect } from "react";
import { Send, BookOpen, Sparkles, Loader2 } from "lucide-react";

interface WorkspaceTaskInputProps {
  onDispatch: (prompt: string) => void;
  onOpenPromptLibrary: () => void;
  isAr?: boolean;
}

export const WorkspaceTaskInput: React.FC<WorkspaceTaskInputProps> = ({
  onDispatch,
  onOpenPromptLibrary,
  isAr = false,
}) => {
  const [inputText, setInputText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const suggestionChips = [
    {
      en: "→ Run case audit for CASE-2026-0042",
      ar: "→ تشغيل تدقيق القضية CASE-2026-0042",
      value: "Run a full judicial case audit for CASE-2026-0042 (Smith v. Gulf Commercial), scanning for evidentiary timeline discrepancies and missing credentials.",
    },
    {
      en: "→ Contract expiry review of Acme Lease",
      ar: "→ مراجعة منتهية لصلاحية اتفاقية عقد إيجار آكمي",
      value: "Perform a post-execution contract review on CON-2026-0117 (Acme Holding lease agreement), focusing on renewal notice window compliance metrics.",
    },
    {
      en: "→ Search clauses about good faith",
      ar: "→ بحث بنود حسن النية",
      value: "Search the clause library for standard Local Venue provisions mentioning Good Faith / Article 246 compliance under the UAE Civil Code.",
    },
  ];

  // Auto-grow textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 180)}px`;
    }
  }, [inputText]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    // Mimic queued animation
    onDispatch(inputText.trim());
    setInputText("");

    setTimeout(() => {
      setIsSubmitting(false);
    }, 1200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChipClick = (val: string) => {
    setInputText(val);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <div className="w-full bg-white/70 backdrop-blur-md rounded-2xl border border-slate-200/60 p-4 shadow-[0_-8px_30px_rgb(15,23,42,0.03)] space-y-3 shrink-0">
      
      {/* Floating Suggested Action Chips above input */}
      <div className={`flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none ${isAr ? "flex-row-reverse" : "flex-row"}`}>
        <span className="text-[10px] font-mono font-black text-slate-400 flex items-center gap-1 shrink-0 uppercase tracking-wider">
          <Sparkles size={11} className="text-amber-500 animate-pulse" />
          <span>{isAr ? "مقترحات:" : "SUGGESTED:"}</span>
        </span>
        <div className={`flex items-center gap-2 ${isAr ? "flex-row-reverse" : ""}`}>
          {suggestionChips.map((chip, idx) => (
            <button
              key={idx}
              onClick={() => handleChipClick(chip.value)}
              className="px-2.5 py-1 text-[10.5px] font-bold rounded-lg border border-slate-150 bg-slate-50 text-slate-705 hover:bg-slate-100 hover:text-slate-900 transition-all cursor-pointer whitespace-nowrap shadow-3xs"
            >
              {isAr ? chip.ar : chip.en}
            </button>
          ))}
        </div>
      </div>

      {/* Styled Composer Input Box */}
      <form onSubmit={handleSubmit} className={`flex items-end gap-3 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
        
        {/* Left Aligned: Prompt Library icon button */}
        <button
          type="button"
          onClick={onOpenPromptLibrary}
          className="p-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-650 hover:text-slate-900 transition-all cursor-pointer flex items-center justify-center shrink-0 shadow-3xs group"
          title={isAr ? "افتح مكتبة النماذج" : "Open prompt library template launcher"}
        >
          <BookOpen size={16} className="group-hover:scale-105 transition-transform" />
          <span className="hidden sm:inline text-xs font-bold pl-1.5 pr-0.5 tracking-wide font-sans">
            {isAr ? "مكتبة النماذج" : "Library"}
          </span>
        </button>

        {/* Dynamic, auto-growing textarea */}
        <div className="flex-1 min-w-0 bg-slate-100/50 hover:bg-slate-100 focus-within:bg-white focus-within:ring-2 focus-within:ring-slate-950/20 border border-slate-200/50 focus-within:border-slate-350 rounded-xl transition-all duration-200">
          <textarea
            data-testid="agent-workspace-input"
            ref={textareaRef}
            rows={1}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isAr
                ? "اسأل الذكاء الاصطناعي، اكتب تدقيقاً، أو صمم عقداً مستقلاً..."
                : "Ask AI Counsel, or command autonomous workspace agents..."
            }
            className="w-full bg-transparent border-0 outline-none p-3 resize-none font-sans text-xs font-semibold leading-relaxed text-slate-800 placeholder-slate-450 h-10 scrollbar-none"
            dir={isAr ? "rtl" : "ltr"}
          />
        </div>

        {/* Right Aligned: Dispatch execution action button */}
        <button
          data-testid="agent-workspace-dispatch"
          type="submit"
          disabled={!inputText.trim() || isSubmitting}
          className="px-5 py-3 h-10 bg-slate-950 hover:bg-slate-900 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold text-xs rounded-xl cursor-pointer transition-all shrink-0 flex items-center gap-1.5 shadow-xs"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={13} className="animate-spin" />
              <span className="font-mono text-[10px] tracking-widest">{isAr ? "جاري الإرسال" : "DISPATCHING"}</span>
            </>
          ) : (
            <>
              <Send size={13} className={isAr ? "rotate-180" : ""} />
              <span className="font-mono text-[10px] tracking-widest">{isAr ? "إرسال" : "DISPATCH"}</span>
            </>
          )}
        </button>

      </form>
      
      <div className={`flex justify-between text-[9.5px] text-slate-400 font-medium ${isAr ? "flex-row-reverse" : "flex-row"}`}>
        <span>
          {isAr
            ? "اضغط Enter للإرسال المباشر. Shift+Enter لسطر جديد."
            : "Press Enter to dispatch directly. Shift+Enter handles newlines."}
        </span>
        <span className="font-mono font-bold tracking-wider text-slate-350">
          {isAr ? "مرخص للنطاق المحلي 100٪" : "100% SECURED LOCAL CONTEXT ENVIRONMENT"}
        </span>
      </div>

    </div>
  );
};
